import React, { useState, useEffect } from 'react';
import { Chip, Icon, Typography } from '@scale/scaleui';
import {
  ConditionType,
  OnboardingFlowTransition,
  TransitionCondition,
} from '@scale/shared/onboardingControlPanel';
import PanelEntry from '../PanelEntry';
import { useFetchAllWorkerSkills } from 'react-scale/hooks/workerSkills/queries/useFetchAllWorkerSkills';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';

interface Props {
  // flowId: string;
  transition: OnboardingFlowTransition;
}

const renderConditionalValues = (
  conditionValue: string | number | boolean | any[],
  index: number,
) => {
  const renderedResults = [];
  if (Array.isArray(conditionValue)) {
    renderedResults.push(<Chip key={`${index} conditional`} label={`has`} />);
    const values = conditionValue.map(val => val.toString());
    renderedResults.push(<Chip key={`${index} values`} label={values.join(', ')} />);
  } else {
    renderedResults.push(<Chip key={`${index} conditional`} label={`is`} />);
    renderedResults.push(<Chip key={`${index} value`} label={conditionValue.toString()} />);
  }
  return renderedResults;
};

const renderTransitionConditions = (
  conditions?: TransitionCondition[],
  workerSkillIdToName?: Record<string, string>,
) => {
  if (!conditions) {
    return <Typography.Paragraph3>No conditions found</Typography.Paragraph3>;
  }
  return (
    <div className="space-y-1">
      {conditions.map((condition, index) => {
        const renderedConditions = [];
        renderedConditions.push(<Typography.Paragraph3>If</Typography.Paragraph3>);
        switch (condition.type) {
          case ConditionType.UserProperty: {
            renderedConditions.push(
              <Chip key={`${index} type`} label={`user property ${condition.key}`} />,
            );
            const renderedConditionalValues = renderConditionalValues(condition.value.value, index);
            renderedConditions.push(...renderedConditionalValues);
            break;
          }
          case ConditionType.StepOutput: {
            renderedConditions.push(<Chip key={`${index} type`} label={`step output`} />);
            const renderedConditionalValues = renderConditionalValues(condition.value.value, index);
            renderedConditions.push(...renderedConditionalValues);
            break;
          }
          case ConditionType.LoadBalancer: {
            renderedConditions.push(<Chip key={`${index} type`} label={`load balancer`} />);
            renderedConditions.push(<Chip key={`${index} conditional`} label={`below capacity`} />);
            renderedConditions.push(
              <Chip key={`${index} capacity`} label={`${condition.capacity}`} />,
            );

            break;
          }
          case ConditionType.WorkerSkill: {
            renderedConditions.push(
              <Chip
                key={`${index} type`}
                label={`worker skill ${
                  workerSkillIdToName
                    ? workerSkillIdToName[condition.id] ?? condition.id
                    : condition.id
                }`}
              />,
            );
            renderedConditions.push(
              <Chip key={`${index} dimension`} label={`${condition.dimension}`} />,
            );
            renderedConditions.push(
              <Chip key={`${index} category`} label={`${condition.category}`} />,
            );
            if (condition.includeInherited) {
              renderedConditions.push(
                <Chip key={`${index} includeInherited`} label={`include inherited`} />,
              );
            }
            break;
          }
          case ConditionType.ResumeMetadata: {
            renderedConditions.push(
              <Chip key={`${index} field`} label={`resume metadata ${condition.field}`} />,
            );
            const renderedConditionalValues = renderConditionalValues(condition.values, index);
            renderedConditions.push(...renderedConditionalValues);
            break;
          }
          case ConditionType.UTM: {
            renderedConditions.push(
              <Chip key={`${index} field`} label={`UTM ${condition.field}`} />,
            );
            const renderedConditionalValues = renderConditionalValues(condition.values, index);
            renderedConditions.push(...renderedConditionalValues);
            break;
          }
          default:
            renderedConditions.push(
              <Typography.Paragraph3>
                no other conditions apply or this is the only transition
              </Typography.Paragraph3>,
            );
            break;
        }
        return (
          <div className="flex items-center space-x-1" key={index}>
            {renderedConditions}
          </div>
        );
      })}
    </div>
  );
};

const DetailPanelTransitionView: React.FC<Props> = ({ transition }) => {
  const [workerSkillIdToName, setWorkerSkillIdToName] = useState<Record<string, string>>({});
  const {
    data: allSkills,
    isLoading: loadingSkills,
    refetch: refetchSkills,
  } = useFetchAllWorkerSkills();
  const { currentFlow, setDetailTarget } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    setDetailTarget: state.setDetailTarget,
  }));

  useEffect(() => {
    if (!allSkills) {
      refetchSkills();
    }
  }, [allSkills, refetchSkills]);

  useEffect(() => {
    if (!loadingSkills && allSkills) {
      const transformedSkills = allSkills.reduce((acc: Record<string, string>, skill) => {
        acc[skill.id] = skill.name;
        return acc;
      }, {});
      setWorkerSkillIdToName(transformedSkills);
    }
  }, [loadingSkills, allSkills]);

  const fullSourceNode = currentFlow?.nodes.find(n => n.name === transition.sourceNode);
  const fullDestNode = currentFlow?.nodes.find(n => n.name === transition.destNode);
  return (
    <>
      <PanelEntry title="Details">
        <div className="flex justify-between items-center">
          <Typography.Paragraph3>Source Node</Typography.Paragraph3>
          <div className="flex items-center gap-1">
            <Typography.Paragraph3>{transition.sourceNode}</Typography.Paragraph3>
            {fullSourceNode && (
              <Icon
                icon="arrow-up-right-from-square"
                size="sm"
                onClick={() => setDetailTarget(fullSourceNode)}
                tooltip="View Node"
              />
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <Typography.Paragraph3>Destination Node</Typography.Paragraph3>
          <div className="flex items-center gap-1">
            <Typography.Paragraph3>{transition.destNode}</Typography.Paragraph3>
            {fullDestNode && (
              <Icon
                icon="arrow-up-right-from-square"
                size="sm"
                onClick={() => setDetailTarget(fullDestNode)}
                tooltip="View Node"
              />
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <Typography.Paragraph3>Conditions</Typography.Paragraph3>
          {renderTransitionConditions(transition.conditions, workerSkillIdToName)}
        </div>
      </PanelEntry>

      <PanelEntry title="Grants">
        <Typography.Paragraph3>Tags</Typography.Paragraph3>
        <div className="grid grid-cols-3">
          {' '}
          {/* Increased gap for better spacing */}
          {transition.tags && transition.tags.length > 0 ? (
            transition.tags.map((tag, index) => (
              <div key={index} className="text-center break-words min-w-0">
                <Typography.Paragraph3 className="bg-gray-300 rounded p-2 text-xs">
                  {tag}
                </Typography.Paragraph3>
              </div>
            ))
          ) : (
            <Typography.Paragraph3 className="col-span-3">
              No Tags on this transition
            </Typography.Paragraph3>
          )}
        </div>
        <Typography.Paragraph3>Worker Skills</Typography.Paragraph3>
        {transition.workerSkills && transition.workerSkills.length > 0 ? (
          <div className="grid grid-cols-3 gap-4 bg-gray-300 p-2 rounded">
            <Typography.Paragraph3 className="flex-1 text-center">Name</Typography.Paragraph3>
            <Typography.Paragraph3 className="flex-1 text-center">Dimension</Typography.Paragraph3>
            <Typography.Paragraph3 className="flex-1 text-center">
              Quality Category
            </Typography.Paragraph3>

            {transition.workerSkills.map((skill, _index) => (
              <>
                <Typography.Paragraph3 className="flex-1 text-center">
                  {skill.name}
                </Typography.Paragraph3>
                <Typography.Paragraph3 className="flex-1 text-center">
                  {skill.dimension}
                </Typography.Paragraph3>
                <Typography.Paragraph3 className="flex-1 text-center">
                  {skill.category}
                </Typography.Paragraph3>
              </>
            ))}
          </div>
        ) : (
          <Typography.Paragraph3>No Worker Skills on this transition</Typography.Paragraph3>
        )}
      </PanelEntry>
    </>
  );
};

export default DetailPanelTransitionView;
